/**
 * ============================================================================
 * KbExtranetAdminPage — Curadoria da Extranet KB (Fase E)
 * ----------------------------------------------------------------------------
 * Rota: /tenant/kb/extranet
 * Acesso restrito a admin do tenant (validado tanto no frontend quanto na RPC).
 *
 * Três abas:
 *   1. Usuários externos — aprovar/rejeitar/desativar, ver detalhes/acessos.
 *   2. Acessos concedidos — visão pivô externo × space/coleção, revogar.
 *   3. Auditoria — log de eventos do portal externo com filtros Sheet + CSV.
 *
 * CHANGELOG:
 *   RELEASE: 2026-07-09 v2.1.0 - Botão "Convidar usuário" no cabeçalho da aba
 *            de Usuários, abrindo KbExtranetInviteUserDialog (Edge Function
 *            kb-extranet-admin-invite).
 *   RELEASE: 2026-07-09 v2.0.0 - Fase E3 concluída:
 *     - Modal de detalhes do usuário (perfil + acessos + histórico).
 *     - Modal de concessão com escopo Space/Coleção e select dependente.
 *     - Sheet de filtros avançados na aba Auditoria (usuário/evento/período).
 *     - Botão "Exportar CSV" do log de auditoria (client-side).
 *     - Estados vazios/erro padronizados via InlineNotice.
 *   RELEASE: 2026-07-09 v1.0.0 - Versão inicial.
 * ============================================================================
 */
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import {
  ArrowLeft,
  CalendarClock,
  CheckCircle2,
  Copy,
  Download,
  FolderTree,
  History,
  Info,
  KeyRound,
  Loader2,
  Mail,
  MoreHorizontal,
  Search,
  Send,
  ShieldCheck,
  SlidersHorizontal,
  Target,
  Trash2,
  User as UserIcon,
  UserCheck,
  UserCircle,
  UserPlus,
  UserX,
  XCircle,
} from "lucide-react";
import { KbExtranetInviteUserDialog } from "@/components/kb/extranet/KbExtranetInviteUserDialog";
import { FormModalSection } from "@/components/shared/FormModalSection";
import { cn } from "@/lib/utils";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetFooter,
} from "@/components/ui/sheet";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { InlineNotice } from "@/components/ui/inline-notice";
import { toast } from "@/hooks/use-toast";
import {
  useKbExtranetUsers,
  useKbExtranetUserDetails,
  useKbExtranetAccesses,
  useKbExtranetAccessLog,
  useKbExtranetSetStatus,
  useKbExtranetGrantAccess,
  useKbExtranetRevokeAccess,
  useKbSpacesForGrant,
  useKbCollectionsForGrant,
  type KbExternalStatus,
  type KbExternalUserRow,
} from "@/hooks/kb/useKbExtranetAdmin";
// [2026-07-10] Reenvio de convite removido — o admin não gera mais links de acesso.
import { useAuth } from "@/hooks/useAuth";

// ---------------------------------------------------------------------------
// Helpers de UI
// ---------------------------------------------------------------------------
const STATUS_LABEL: Record<KbExternalStatus, string> = {
  pending: "Pendente",
  active: "Ativo",
  rejected: "Rejeitado",
  expired: "Expirado",
};
const STATUS_VARIANT: Record<KbExternalStatus, "default" | "secondary" | "destructive" | "outline"> = {
  pending: "secondary",
  active: "default",
  rejected: "destructive",
  expired: "outline",
};
function StatusBadge({ status }: { status: KbExternalStatus }) {
  return <Badge variant={STATUS_VARIANT[status]}>{STATUS_LABEL[status]}</Badge>;
}
function fmt(dt: string | null | undefined): string {
  if (!dt) return "—";
  try {
    return format(new Date(dt), "dd/MM/yyyy HH:mm", { locale: ptBR });
  } catch {
    return "—";
  }
}

// ---------------------------------------------------------------------------
// Exporta uma lista de linhas para CSV (client-side, sem libs externas).
// Trata aspas duplas conforme RFC 4180 e força UTF-8 BOM p/ Excel BR.
// ---------------------------------------------------------------------------
function exportRowsToCsv(filename: string, rows: Record<string, unknown>[]) {
  if (!rows.length) {
    toast({ title: "Nada para exportar", description: "Nenhuma linha visível." });
    return;
  }
  const headers = Object.keys(rows[0]);
  const escape = (v: unknown) => {
    if (v === null || v === undefined) return "";
    const s = String(v).replace(/"/g, '""');
    return /[",;\n]/.test(s) ? `"${s}"` : s;
  };
  const csv = [
    headers.join(";"),
    ...rows.map((r) => headers.map((h) => escape(r[h])).join(";")),
  ].join("\n");
  const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

// ===========================================================================
// Página principal
// ===========================================================================
export default function KbExtranetAdminPage() {
  const [tab, setTab] = useState<"users" | "access" | "audit">("users");
  const [grantOpen, setGrantOpen] = useState(false);
  const [grantForUser, setGrantForUser] = useState<KbExternalUserRow | null>(null);
  const [rejectFor, setRejectFor] = useState<KbExternalUserRow | null>(null);
  const [rejectReason, setRejectReason] = useState("");
  const [detailsFor, setDetailsFor] = useState<KbExternalUserRow | null>(null);
  const [inviteOpen, setInviteOpen] = useState(false);
  // [2026-07-10] Reenvio de convite removido: o admin apenas pré-cadastra o
  // usuário e compartilha o link público. Não geramos mais tokens de acesso.

  const { currentTenant } = useAuth();
  const setStatusMut = useKbExtranetSetStatus();
  const revokeMut = useKbExtranetRevokeAccess();

  const openGrant = (u: KbExternalUserRow) => {
    setGrantForUser(u);
    setGrantOpen(true);
  };

  const handleReject = async () => {
    if (!rejectFor) return;
    await setStatusMut.mutateAsync({
      externalUserId: rejectFor.id,
      status: "rejected",
      reason: rejectReason || undefined,
    });
    setRejectFor(null);
    setRejectReason("");
  };

  // Copia o link público de cadastro/login da Extranet deste tenant.
  const copyExtranetLink = async (path: "cadastro" | "login") => {
    if (!currentTenant?.slug) return;
    const url = `${window.location.origin}/extranet/${currentTenant.slug}/${path}`;
    try {
      await navigator.clipboard.writeText(url);
      toast({ title: "Link copiado", description: url });
    } catch {
      toast({ title: "Não foi possível copiar", variant: "destructive" });
    }
  };

  return (
    <div className="flex h-full flex-col gap-4 p-4 md:p-6">
      {/* Header */}
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" asChild>
            <Link to="/tenant/kb" aria-label="Voltar">
              <ArrowLeft className="h-4 w-4" />
            </Link>
          </Button>
          <div>
            <h1 className="text-2xl font-semibold">Extranet — Usuários externos</h1>
            <p className="text-sm text-muted-foreground">
              Aprove cadastros, gerencie acessos por espaço/coleção e audite os eventos do portal do cliente.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => copyExtranetLink("cadastro")}>
            <Copy className="h-4 w-4 mr-2" />
            Copiar link de cadastro
          </Button>
          <Button onClick={() => setInviteOpen(true)}>
            <UserPlus className="h-4 w-4 mr-2" />
            Novo usuário externo
          </Button>
        </div>
      </div>

      <Tabs value={tab} onValueChange={(v) => setTab(v as any)}>
        <TabsList>
          <TabsTrigger value="users">Usuários</TabsTrigger>
          <TabsTrigger value="access">Acessos concedidos</TabsTrigger>
          <TabsTrigger value="audit">Auditoria</TabsTrigger>
        </TabsList>

        <TabsContent value="users">
          <UsersTab
            onDetails={(u) => setDetailsFor(u)}
            onGrant={openGrant}
            onReject={(u) => setRejectFor(u)}
            onApprove={(u) =>
              setStatusMut.mutate({ externalUserId: u.id, status: "active" })
            }
            onDisable={(u) =>
              setStatusMut.mutate({ externalUserId: u.id, status: "pending" })
            }
            onExpire={(u) =>
              setStatusMut.mutate({ externalUserId: u.id, status: "expired" })
            }
            onCopyLoginLink={() => copyExtranetLink("login")}
          />
        </TabsContent>

        <TabsContent value="access">
          <AccessesTab onRevoke={(id) => revokeMut.mutate(id)} />
        </TabsContent>

        <TabsContent value="audit">
          <AuditTab />
        </TabsContent>
      </Tabs>

      {/* Modal: convidar novo usuário externo */}
      <KbExtranetInviteUserDialog open={inviteOpen} onOpenChange={setInviteOpen} />


      {/* Modal: conceder acesso */}
      <GrantAccessDialog
        open={grantOpen}
        user={grantForUser}
        onOpenChange={(o) => {
          setGrantOpen(o);
          if (!o) setGrantForUser(null);
        }}
      />

      {/* Modal: detalhes do usuário externo */}
      <UserDetailsDialog
        user={detailsFor}
        onClose={() => setDetailsFor(null)}
        onGrant={(u) => {
          setDetailsFor(null);
          openGrant(u);
        }}
        onRevokeAccess={(id) => revokeMut.mutate(id)}
      />

      {/* Modal: rejeitar */}
      <Dialog open={!!rejectFor} onOpenChange={(o) => !o && setRejectFor(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Rejeitar acesso</DialogTitle>
            <DialogDescription>
              O usuário {rejectFor?.display_name ?? rejectFor?.email} será marcado como rejeitado e
              perderá o acesso ao portal.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="reject-reason">Motivo (opcional)</Label>
            <Input
              id="reject-reason"
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Ex.: cadastro duplicado, empresa não elegível..."
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectFor(null)}>Cancelar</Button>
            <Button variant="destructive" onClick={handleReject} disabled={setStatusMut.isPending}>
              {setStatusMut.isPending && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
              Rejeitar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* [2026-07-10] Modal de reenvio de convite removido: por segurança, o
          sistema não gera links de acesso a partir do painel admin. Se o
          usuário perder o link/e-mail, o admin apenas compartilha novamente
          a URL pública da Extranet (item "Copiar link de cadastro" no menu). */}
    </div>
  );
}

// ===========================================================================
// Aba: Usuários
// ===========================================================================
function UsersTab({
  onApprove,
  onReject,
  onDisable,
  onExpire,
  onGrant,
  onDetails,
  onCopyLoginLink,
}: {
  onApprove: (u: KbExternalUserRow) => void;
  onReject: (u: KbExternalUserRow) => void;
  onDisable: (u: KbExternalUserRow) => void;
  onExpire: (u: KbExternalUserRow) => void;
  onGrant: (u: KbExternalUserRow) => void;
  onDetails: (u: KbExternalUserRow) => void;
  onCopyLoginLink: () => void;
}) {
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState<KbExternalStatus | "all">("all");
  const { data: users = [], isLoading, error } = useKbExtranetUsers({ search, status });

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Buscar por nome, e-mail ou organização..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-8"
          />
        </div>
        <Select value={status} onValueChange={(v) => setStatus(v as any)}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos os status</SelectItem>
            <SelectItem value="pending">Pendente</SelectItem>
            <SelectItem value="active">Ativo</SelectItem>
            <SelectItem value="rejected">Rejeitado</SelectItem>
            <SelectItem value="expired">Expirado</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {error && (
        <InlineNotice variant="error">
          Falha ao carregar usuários externos. Tente novamente em instantes.
        </InlineNotice>
      )}

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Usuário</TableHead>
              <TableHead>Organização</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Acessos</TableHead>
              <TableHead>Último login</TableHead>
              <TableHead>Criado em</TableHead>
              <TableHead className="w-[60px]" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading && (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-10">
                  <Loader2 className="h-5 w-5 animate-spin inline mr-2" /> Carregando...
                </TableCell>
              </TableRow>
            )}
            {!isLoading && users.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} className="py-6">
                  <InlineNotice variant="info" icon={Info}>
                    Nenhum usuário externo encontrado com os filtros atuais.
                  </InlineNotice>
                </TableCell>
              </TableRow>
            )}
            {users.map((u) => (
              <TableRow
                key={u.id}
                className="cursor-pointer hover:bg-muted/30"
                onClick={() => onDetails(u)}
              >
                <TableCell>
                  <div className="font-medium">{u.display_name ?? "—"}</div>
                  <div className="text-xs text-muted-foreground">{u.email ?? "—"}</div>
                </TableCell>
                <TableCell>{u.organization ?? "—"}</TableCell>
                <TableCell><StatusBadge status={u.status} /></TableCell>
                <TableCell>{u.access_count}</TableCell>
                <TableCell className="text-sm">{fmt(u.last_login_at)}</TableCell>
                <TableCell className="text-sm">{fmt(u.created_at)}</TableCell>
                <TableCell onClick={(e) => e.stopPropagation()}>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon">
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => onDetails(u)}>
                        <Info className="h-4 w-4 mr-2" /> Detalhes
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      {u.status !== "active" && (
                        <DropdownMenuItem onClick={() => onApprove(u)}>
                          <UserCheck className="h-4 w-4 mr-2" /> Aprovar/Ativar
                        </DropdownMenuItem>
                      )}
                      {u.status === "active" && (
                        <DropdownMenuItem onClick={() => onDisable(u)}>
                          <UserX className="h-4 w-4 mr-2" /> Desativar
                        </DropdownMenuItem>
                      )}
                      {u.status !== "rejected" && (
                        <DropdownMenuItem onClick={() => onReject(u)}>
                          <XCircle className="h-4 w-4 mr-2" /> Rejeitar
                        </DropdownMenuItem>
                      )}
                      {u.status !== "expired" && (
                        <DropdownMenuItem onClick={() => onExpire(u)}>
                          <XCircle className="h-4 w-4 mr-2" /> Marcar como expirado
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={() => onCopyLoginLink()}>
                        <Copy className="h-4 w-4 mr-2" /> Copiar link do portal
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => onGrant(u)}>
                        <ShieldCheck className="h-4 w-4 mr-2" /> Conceder acesso
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

// ===========================================================================
// Aba: Acessos concedidos
// ===========================================================================
function AccessesTab({ onRevoke }: { onRevoke: (id: string) => void }) {
  const { data: rows = [], isLoading, error } = useKbExtranetAccesses();
  return (
    <div className="space-y-3">
      {error && (
        <InlineNotice variant="error">
          Falha ao carregar acessos concedidos.
        </InlineNotice>
      )}
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Usuário</TableHead>
              <TableHead>Escopo</TableHead>
              <TableHead>Nível</TableHead>
              <TableHead>Expira em</TableHead>
              <TableHead>Concedido em</TableHead>
              <TableHead className="w-[60px]" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading && (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-10">
                  <Loader2 className="h-5 w-5 animate-spin inline mr-2" /> Carregando...
                </TableCell>
              </TableRow>
            )}
            {!isLoading && rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} className="py-6">
                  <InlineNotice variant="info" icon={Info}>
                    Nenhum acesso concedido até o momento.
                  </InlineNotice>
                </TableCell>
              </TableRow>
            )}
            {rows.map((r) => (
              <TableRow key={r.id}>
                <TableCell>
                  <div className="font-medium">{r.external_user_display_name ?? "—"}</div>
                  <div className="text-xs text-muted-foreground">{r.external_user_email ?? "—"}</div>
                </TableCell>
                <TableCell>
                  {r.space_id ? (
                    <span><Badge variant="secondary">Space</Badge> {r.space_name}</span>
                  ) : (
                    <span><Badge variant="outline">Coleção</Badge> {r.collection_name}</span>
                  )}
                </TableCell>
                <TableCell>
                  <Badge variant={r.access_level === "comment" ? "default" : "secondary"}>
                    {r.access_level === "comment" ? "Comentar" : "Leitura"}
                  </Badge>
                </TableCell>
                <TableCell className="text-sm">{fmt(r.expires_at)}</TableCell>
                <TableCell className="text-sm">{fmt(r.granted_at)}</TableCell>
                <TableCell>
                  <Button variant="ghost" size="icon" onClick={() => onRevoke(r.id)}>
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

// ===========================================================================
// Aba: Auditoria (com Sheet de filtros avançados + Export CSV)
// ===========================================================================
function AuditTab() {
  const [eventType, setEventType] = useState<string>("all");
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [userSearch, setUserSearch] = useState("");
  const [dateFrom, setDateFrom] = useState<string>("");
  const [dateTo, setDateTo] = useState<string>("");
  const [limit, setLimit] = useState<number>(200);

  const { data: rows = [], isLoading, error } = useKbExtranetAccessLog({
    eventType: eventType === "all" ? null : eventType,
    limit,
  });

  // Filtragem client-side por usuário e período (a RPC não expõe esses params).
  const filtered = useMemo(() => {
    const term = userSearch.trim().toLowerCase();
    const from = dateFrom ? new Date(dateFrom).getTime() : null;
    const to = dateTo ? new Date(dateTo).getTime() + 86_399_000 : null;
    return rows.filter((r) => {
      if (term) {
        const hay = `${r.external_user_display_name ?? ""} ${r.external_user_email ?? ""}`.toLowerCase();
        if (!hay.includes(term)) return false;
      }
      if (from || to) {
        const t = new Date(r.created_at).getTime();
        if (from && t < from) return false;
        if (to && t > to) return false;
      }
      return true;
    });
  }, [rows, userSearch, dateFrom, dateTo]);

  const activeFiltersCount =
    (userSearch ? 1 : 0) + (dateFrom ? 1 : 0) + (dateTo ? 1 : 0) + (limit !== 200 ? 1 : 0);

  const handleExport = () => {
    exportRowsToCsv(
      `extranet_auditoria_${new Date().toISOString().slice(0, 10)}.csv`,
      filtered.map((r) => ({
        quando: fmt(r.created_at),
        usuario: r.external_user_display_name ?? "",
        email: r.external_user_email ?? "",
        evento: r.event_type,
        space: r.space_name ?? "",
        ip: r.ip_address ?? "",
      })),
    );
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <Select value={eventType} onValueChange={setEventType}>
          <SelectTrigger className="w-[220px]">
            <SelectValue placeholder="Tipo de evento" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos os eventos</SelectItem>
            <SelectItem value="login">Login</SelectItem>
            <SelectItem value="logout">Logout</SelectItem>
            <SelectItem value="login_failed">Login falhou</SelectItem>
            <SelectItem value="page_view">Página visualizada</SelectItem>
            <SelectItem value="page_denied">Página negada</SelectItem>
            <SelectItem value="space_view">Space visualizado</SelectItem>
            <SelectItem value="space_denied">Space negado</SelectItem>
          </SelectContent>
        </Select>

        <Button variant="outline" size="sm" onClick={() => setFiltersOpen(true)}>
          <SlidersHorizontal className="h-4 w-4 mr-1" /> Mais filtros
          {activeFiltersCount > 0 && (
            <Badge className="ml-2" variant="secondary">{activeFiltersCount}</Badge>
          )}
        </Button>

        <div className="flex-1" />

        <Button variant="outline" size="sm" onClick={handleExport}>
          <Download className="h-4 w-4 mr-1" /> Exportar CSV
        </Button>
      </div>

      {error && (
        <InlineNotice variant="error">
          Falha ao carregar auditoria da extranet.
        </InlineNotice>
      )}

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Quando</TableHead>
              <TableHead>Usuário</TableHead>
              <TableHead>Evento</TableHead>
              <TableHead>Space</TableHead>
              <TableHead>IP</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading && (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-10">
                  <Loader2 className="h-5 w-5 animate-spin inline mr-2" /> Carregando...
                </TableCell>
              </TableRow>
            )}
            {!isLoading && filtered.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} className="py-6">
                  <InlineNotice variant="info" icon={Info}>
                    Nenhum evento encontrado com os filtros atuais.
                  </InlineNotice>
                </TableCell>
              </TableRow>
            )}
            {filtered.map((r) => (
              <TableRow key={r.id}>
                <TableCell className="text-sm">{fmt(r.created_at)}</TableCell>
                <TableCell>
                  <div className="font-medium">{r.external_user_display_name ?? "—"}</div>
                  <div className="text-xs text-muted-foreground">{r.external_user_email ?? "—"}</div>
                </TableCell>
                <TableCell><Badge variant="outline">{r.event_type}</Badge></TableCell>
                <TableCell className="text-sm">{r.space_name ?? "—"}</TableCell>
                <TableCell className="text-sm text-muted-foreground">{r.ip_address ?? "—"}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Sheet — filtros avançados de auditoria */}
      <Sheet open={filtersOpen} onOpenChange={setFiltersOpen}>
        <SheetContent side="right" className="w-full sm:max-w-md">
          <SheetHeader>
            <SheetTitle>Filtros avançados</SheetTitle>
            <SheetDescription>
              Restrinja o log por usuário, período e volume máximo de registros carregados.
            </SheetDescription>
          </SheetHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-1">
              <Label htmlFor="flt-user">Usuário (nome ou e-mail)</Label>
              <Input
                id="flt-user"
                value={userSearch}
                onChange={(e) => setUserSearch(e.target.value)}
                placeholder="Ex.: cliente@empresa.com"
              />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1">
                <Label htmlFor="flt-from">De</Label>
                <Input id="flt-from" type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
              </div>
              <div className="space-y-1">
                <Label htmlFor="flt-to">Até</Label>
                <Input id="flt-to" type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
              </div>
            </div>
            <div className="space-y-1">
              <Label>Máximo de registros</Label>
              <Select value={String(limit)} onValueChange={(v) => setLimit(Number(v))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="100">100</SelectItem>
                  <SelectItem value="200">200</SelectItem>
                  <SelectItem value="500">500</SelectItem>
                  <SelectItem value="1000">1000</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <SheetFooter>
            <Button
              variant="outline"
              onClick={() => {
                setUserSearch("");
                setDateFrom("");
                setDateTo("");
                setLimit(200);
              }}
            >
              Limpar filtros
            </Button>
            <Button onClick={() => setFiltersOpen(false)}>Aplicar</Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>
    </div>
  );
}

// ===========================================================================
// Modal: Detalhes do usuário externo
// ---------------------------------------------------------------------------
// Mostra perfil completo, acessos vigentes e histórico recente do log.
// ===========================================================================
function UserDetailsDialog({
  user,
  onClose,
  onGrant,
  onRevokeAccess,
}: {
  user: KbExternalUserRow | null;
  onClose: () => void;
  onGrant: (u: KbExternalUserRow) => void;
  onRevokeAccess: (id: string) => void;
}) {
  const open = !!user;
  const { data: details, isLoading } = useKbExtranetUserDetails(user?.id ?? null);
  const { data: log = [], isLoading: loadingLog } = useKbExtranetAccessLog({
    externalUserId: user?.id ?? null,
    limit: 50,
  });

  const accesses = (details?.accesses ?? []) as Array<{
    id: string;
    space_id: string | null;
    space_name: string | null;
    collection_id: string | null;
    collection_name: string | null;
    access_level: string;
    expires_at: string | null;
    granted_at: string;
  }>;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      {/* Shell premium alinhado ao GrantAccessDialog:
          header em degradê + corpo rolável + rodapé fixo. */}
      <DialogContent className="max-w-3xl w-[95vw] h-[90dvh] md:h-auto md:max-h-[85dvh] flex flex-col p-0 gap-0 overflow-hidden sm:rounded-lg">
        {/* Capa premium com identidade do usuário */}
        <DialogHeader className="px-4 sm:px-6 py-3 sm:py-4 border-b bg-gradient-to-r from-primary/5 via-primary/3 to-transparent shrink-0">
          <div className="flex items-start gap-3 min-w-0 pr-8">
            <div className="p-1.5 sm:p-2 bg-primary/10 rounded-lg shrink-0">
              <UserCircle className="h-4 w-4 sm:h-5 sm:w-5 text-primary" />
            </div>
            <div className="flex flex-col min-w-0">
              <DialogTitle className="truncate">
                {user?.display_name ?? user?.email ?? "Usuário externo"}
              </DialogTitle>
              <DialogDescription className="text-xs line-clamp-2 sm:line-clamp-none">
                Perfil completo, acessos vigentes e histórico do portal.
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        {/* Corpo rolável com seções padronizadas */}
        <div className="flex-1 min-h-0 overflow-y-auto px-6 py-5 space-y-6">
          {isLoading ? (
            <div className="py-10 text-center">
              <Loader2 className="h-5 w-5 animate-spin inline mr-2" /> Carregando...
            </div>
          ) : (
            <>
              {/* Perfil */}
              <FormModalSection icon={UserIcon} title="Perfil">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                  <div><span className="text-muted-foreground">E-mail:</span> {user?.email ?? "—"}</div>
                  <div><span className="text-muted-foreground">Organização:</span> {user?.organization ?? "—"}</div>
                  <div className="flex items-center gap-2"><span className="text-muted-foreground">Status:</span> {user && <StatusBadge status={user.status} />}</div>
                  <div><span className="text-muted-foreground">Tipo:</span> {details?.external_type ?? "—"}</div>
                  <div><span className="text-muted-foreground">Criado em:</span> {fmt(user?.created_at)}</div>
                  <div><span className="text-muted-foreground">Último login:</span> {fmt(user?.last_login_at)}</div>
                  <div><span className="text-muted-foreground">Aprovado em:</span> {fmt(details?.approved_at)}</div>
                  <div><span className="text-muted-foreground">Expira em:</span> {fmt(details?.expires_at)}</div>
                  {details?.rejected_reason && (
                    <div className="sm:col-span-2">
                      <InlineNotice variant="warning">
                        Motivo de rejeição: {details.rejected_reason}
                      </InlineNotice>
                    </div>
                  )}
                </div>
              </FormModalSection>

              {/* Acessos vigentes — botão "Conceder novo" alinhado à direita
                  do cabeçalho (padrão premium). */}
              <FormModalSection icon={KeyRound} title="Acessos concedidos">
                {user && (
                  <div className="flex justify-end -mt-2">
                    <Button size="sm" variant="outline" onClick={() => onGrant(user)}>
                      <ShieldCheck className="h-4 w-4 mr-1" /> Conceder novo
                    </Button>
                  </div>
                )}
                {accesses.length === 0 ? (
                  <InlineNotice variant="info" icon={Info}>
                    Este usuário ainda não possui nenhum acesso liberado.
                  </InlineNotice>
                ) : (
                  <div className="rounded-md border">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Escopo</TableHead>
                          <TableHead>Nível</TableHead>
                          <TableHead>Expira em</TableHead>
                          <TableHead>Concedido em</TableHead>
                          <TableHead className="w-[60px]" />
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {accesses.map((a) => (
                          <TableRow key={a.id}>
                            <TableCell>
                              {a.space_id ? (
                                <><Badge variant="secondary">Space</Badge> {a.space_name}</>
                              ) : (
                                <><Badge variant="outline">Coleção</Badge> {a.collection_name}</>
                              )}
                            </TableCell>
                            <TableCell>
                              <Badge variant={a.access_level === "comment" ? "default" : "secondary"}>
                                {a.access_level === "comment" ? "Comentar" : "Leitura"}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-sm">{fmt(a.expires_at)}</TableCell>
                            <TableCell className="text-sm">{fmt(a.granted_at)}</TableCell>
                            <TableCell>
                              <Button variant="ghost" size="icon" onClick={() => onRevokeAccess(a.id)}>
                                <Trash2 className="h-4 w-4 text-destructive" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </FormModalSection>

              {/* Histórico recente */}
              <FormModalSection icon={History} title="Histórico recente">
                {loadingLog ? (
                  <div className="py-6 text-center text-sm text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin inline mr-2" /> Carregando histórico...
                  </div>
                ) : log.length === 0 ? (
                  <InlineNotice variant="info" icon={Info}>
                    Ainda não há eventos registrados para este usuário.
                  </InlineNotice>
                ) : (
                  <div className="rounded-md border max-h-64 overflow-y-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Quando</TableHead>
                          <TableHead>Evento</TableHead>
                          <TableHead>Space</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {log.map((r) => (
                          <TableRow key={r.id}>
                            <TableCell className="text-sm">{fmt(r.created_at)}</TableCell>
                            <TableCell><Badge variant="outline">{r.event_type}</Badge></TableCell>
                            <TableCell className="text-sm">{r.space_name ?? "—"}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </FormModalSection>
            </>
          )}
        </div>

        {/* Rodapé fixo com ação de fechar */}
        <div className="border-t px-6 py-3 flex items-center justify-end gap-2 shrink-0 bg-background">
          <Button variant="outline" onClick={onClose}>Fechar</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ===========================================================================
// Modal: conceder acesso (com escopo Space | Coleção)
// ===========================================================================
function GrantAccessDialog({
  open,
  onOpenChange,
  user,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  user: KbExternalUserRow | null;
}) {
  const [scope, setScope] = useState<"space" | "collection">("space");
  const [spaceId, setSpaceId] = useState<string>("");
  const [collectionId, setCollectionId] = useState<string>("");
  const [accessLevel, setAccessLevel] = useState<"read" | "comment">("read");
  const [expiresAt, setExpiresAt] = useState<string>("");
  const { data: spaces = [] } = useKbSpacesForGrant();
  const { data: collections = [] } = useKbCollectionsForGrant(spaceId || null);
  const grantMut = useKbExtranetGrantAccess();

  const canSubmit = useMemo(() => {
    if (!user || !spaceId) return false;
    if (scope === "collection" && !collectionId) return false;
    return true;
  }, [scope, spaceId, collectionId, user]);

  const handleGrant = async () => {
    if (!user || !canSubmit) return;
    try {
      await grantMut.mutateAsync({
        externalUserId: user.id,
        // Escopo mutuamente exclusivo (a RPC valida também no backend)
        spaceId: scope === "space" ? spaceId : null,
        collectionId: scope === "collection" ? collectionId : null,
        accessLevel,
        expiresAt: expiresAt ? new Date(expiresAt).toISOString() : null,
      });
      onOpenChange(false);
      setSpaceId("");
      setCollectionId("");
      setExpiresAt("");
      setAccessLevel("read");
      setScope("space");
    } catch {
      // toast já tratado no hook
    }
  };

  // Cards clicáveis para o escopo (padrão TaskFormDialog)
  const scopeOptions = [
    {
      value: "space" as const,
      icon: ShieldCheck,
      title: "Espaço inteiro",
      hint: "Acesso a todas as coleções do espaço.",
    },
    {
      value: "collection" as const,
      icon: FolderTree,
      title: "Coleção específica",
      hint: "Restringe acesso a uma coleção do espaço.",
    },
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl w-[95vw] h-[90dvh] md:h-auto md:max-h-[85dvh] flex flex-col p-0 gap-0 overflow-hidden sm:rounded-lg">
        {/* Capa premium padronizada (mesmo padrão do modal de anotações) */}
        <DialogHeader className="px-4 sm:px-6 py-3 sm:py-4 border-b bg-gradient-to-r from-primary/5 via-primary/3 to-transparent shrink-0">
          <div className="flex items-start gap-3 min-w-0 pr-8">
            <div className="p-1.5 sm:p-2 bg-primary/10 rounded-lg shrink-0">
              <KeyRound className="h-4 w-4 sm:h-5 sm:w-5 text-primary" />
            </div>
            <div className="flex flex-col min-w-0">
              <DialogTitle className="truncate">Conceder acesso</DialogTitle>
              <DialogDescription className="text-xs line-clamp-2 sm:line-clamp-none">
                Libere ao usuário externo acesso a um espaço ou coleção específica da base de conhecimento.
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        {/* Corpo rolável com seções padronizadas */}
        <div className="flex-1 min-h-0 overflow-y-auto px-6 py-5 space-y-6">
          {/* Usuário — bloco read-only estilo TaskFormDialog */}
          <FormModalSection icon={UserIcon} title="Usuário">
            <div className="rounded-lg border bg-muted/30 p-3">
              <div className="text-sm font-medium text-foreground">
                {user?.display_name ?? "—"}
              </div>
              {user?.email && (
                <div className="text-xs text-muted-foreground mt-0.5">
                  {user.email}
                </div>
              )}
            </div>
          </FormModalSection>

          {/* Escopo e destino */}
          <FormModalSection
            icon={Target}
            title="Escopo e destino"
            description="Escolha se o acesso vale para o espaço inteiro ou apenas para uma coleção."
          >
            <RadioGroup
              value={scope}
              onValueChange={(v) => {
                setScope(v as "space" | "collection");
                setCollectionId("");
              }}
              className="grid grid-cols-1 sm:grid-cols-2 gap-3"
            >
              {scopeOptions.map((opt) => {
                const Icon = opt.icon;
                const selected = scope === opt.value;
                return (
                  <label
                    key={opt.value}
                    htmlFor={`scope-${opt.value}`}
                    className={cn(
                      "relative flex items-start gap-3 rounded-lg border p-3 cursor-pointer transition-colors",
                      selected
                        ? "border-primary bg-primary/5 ring-1 ring-primary/30"
                        : "border-border hover:bg-muted/50",
                    )}
                  >
                    <RadioGroupItem
                      value={opt.value}
                      id={`scope-${opt.value}`}
                      className="mt-0.5"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <Icon
                          className={cn(
                            "h-4 w-4",
                            selected ? "text-primary" : "text-muted-foreground",
                          )}
                        />
                        <span className="text-sm font-medium">{opt.title}</span>
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">
                        {opt.hint}
                      </p>
                    </div>
                  </label>
                );
              })}
            </RadioGroup>

            <div className="space-y-1.5">
              <Label>Espaço</Label>
              <Select
                value={spaceId}
                onValueChange={(v) => {
                  setSpaceId(v);
                  setCollectionId("");
                }}
              >
                <SelectTrigger className="h-10">
                  <SelectValue placeholder="Selecione um espaço" />
                </SelectTrigger>
                <SelectContent>
                  {spaces.map((s) => (
                    <SelectItem key={s.id} value={s.id}>
                      {s.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {scope === "collection" && (
              <div className="space-y-1.5">
                <Label>Coleção</Label>
                <Select
                  value={collectionId}
                  onValueChange={setCollectionId}
                  disabled={!spaceId}
                >
                  <SelectTrigger className="h-10">
                    <SelectValue
                      placeholder={
                        spaceId
                          ? "Selecione uma coleção"
                          : "Escolha um espaço primeiro"
                      }
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {collections.length === 0 && spaceId && (
                      <div className="px-2 py-3 text-sm text-muted-foreground">
                        Este espaço não possui coleções.
                      </div>
                    )}
                    {collections.map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </FormModalSection>

          {/* Permissão e validade */}
          <FormModalSection icon={ShieldCheck} title="Permissão e validade">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>Nível de acesso</Label>
                <Select
                  value={accessLevel}
                  onValueChange={(v) => setAccessLevel(v as any)}
                >
                  <SelectTrigger className="h-10">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="read">Leitura</SelectItem>
                    <SelectItem value="comment">Comentar</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="expires_at" className="flex items-center gap-1.5">
                  <CalendarClock className="h-3.5 w-3.5 text-muted-foreground" />
                  Expira em (opcional)
                </Label>
                <Input
                  id="expires_at"
                  type="datetime-local"
                  value={expiresAt}
                  onChange={(e) => setExpiresAt(e.target.value)}
                  className="h-10"
                />
              </div>
            </div>
            <p className="text-xs text-muted-foreground">
              Sem data = acesso permanente. Ao vencer, o usuário perde a leitura automaticamente.
            </p>
          </FormModalSection>
        </div>

        {/* Rodapé sticky (padrão TaskFormDialog) */}
        <div className="border-t bg-muted/30 px-6 py-3 flex items-center justify-end gap-2 shrink-0">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button
            onClick={handleGrant}
            disabled={!canSubmit || grantMut.isPending}
          >
            {grantMut.isPending ? (
              <Loader2 className="h-4 w-4 mr-1 animate-spin" />
            ) : (
              <CheckCircle2 className="h-4 w-4 mr-1" />
            )}
            Conceder
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
